// apps/tutorify/src/modules/logify/hooks/useObservations.ts
import { useCallback } from "react";
import { useSWRConfig } from "swr";
import { useRestQuery } from "../../../hooks/useRestQuery";
import { useRestMutation } from "../../../hooks/useRestMutation";
import * as api from "../services/logify.api";
import type {
  Observation,
  CreateObservationPayload,
  UpdateObservationPayload,
  ObservationFilters,
} from "../types";

function isLogifyKey(key: unknown): boolean {
  return Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith("logify");
}

export function useObservations(filters: ObservationFilters = {}, { skipFetch = false } = {}) {
  const { mutate: globalMutate } = useSWRConfig();

  const {
    data: observations,
    loading,
    error,
    refetch,
  } = useRestQuery<Observation[]>(
    () => api.getObservations(filters),
    {
      key: "logify-observations",
      skip: skipFetch,
      deps: [
        filters.classRoomId,
        filters.studentId,
        filters.category,
        filters.sentiment,
        filters.startDate,
        filters.endDate,
        filters.source,
        filters.limit,
        filters.offset,
      ],
    }
  );

  const invalidateAll = useCallback(() => {
    globalMutate(isLogifyKey, undefined, { revalidate: true });
  }, [globalMutate]);

  const [triggerCreate, createState] = useRestMutation(
    useCallback((payload: CreateObservationPayload) => api.createObservation(payload), [])
  );

  const [triggerUpdate, updateState] = useRestMutation(
    useCallback(
      ({ id, payload }: { id: number; payload: UpdateObservationPayload }) =>
        api.updateObservation(id, payload),
      []
    )
  );

  const [triggerDelete, deleteState] = useRestMutation(
    useCallback((id: number) => api.deleteObservation(id), [])
  );

  const createObservation = useCallback(
    async (payload: CreateObservationPayload) => {
      const result = await triggerCreate(payload);
      if (result) invalidateAll();
      return result;
    },
    [triggerCreate, invalidateAll]
  );

  const updateObservation = useCallback(
    async (id: number, payload: UpdateObservationPayload) => {
      const result = await triggerUpdate({ id, payload });
      if (result) invalidateAll();
      return result;
    },
    [triggerUpdate, invalidateAll]
  );

  const deleteObservation = useCallback(
    async (id: number) => {
      const result = await triggerDelete(id);
      if (result) invalidateAll();
      return result;
    },
    [triggerDelete, invalidateAll]
  );

  return {
    observations: observations ?? [],
    loading,
    error,
    refetch,
    invalidateAll,
    createObservation,
    updateObservation,
    deleteObservation,
    creating: createState.loading,
    updating: updateState.loading,
    deleting: deleteState.loading,
  };
}
